import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import {
  type BlurEvent,
  Pressable,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import TextInput from '@/components/UI/TextInput';
import Icon from '@/components/Unistyles/Icon';

const YEARS_IN_FUTURE = 5;
const YEARS_IN_PAST = 20;
const displayedYears = Array.from(
  {
    length: YEARS_IN_PAST,
  },
  (_, i) =>
    (
      DateTime.local().plus({
        years: YEARS_IN_FUTURE,
      }).year - i
    ).toString(),
);
function parseIntWithFallback(value: string, fallback: number) {
  const parsed = Number.parseInt(value, 10);
  if (parsed) {
    return parsed;
  }
  return fallback;
}
function getDaysInMonth({
  month,
  year,
  fallbackMonth = 1,
  fallbackYear,
}: {
  month: string;
  year: string;
  fallbackMonth?: number;
  fallbackYear: number;
}) {
  const daysInMonth = DateTime.fromObject({
    month: parseIntWithFallback(month, fallbackMonth),
    year: parseIntWithFallback(year, fallbackYear),
  }).daysInMonth;
  if (daysInMonth) {
    return daysInMonth;
  }
  return 31;
}
type DateTimeValues = {
  month: string;
  day: string;
  year: string;
  hour: string;
  minute: string;
  meridiem: string;
};
function parseISOToComponents(isoString: string | null): DateTimeValues {
  if (!isoString) {
    return {
      month: '',
      day: '',
      year: '',
      hour: '',
      minute: '',
      meridiem: 'AM',
    };
  }
  try {
    const dt = DateTime.fromISO(isoString);
    const hour12 = dt.hour === 0 ? 12 : dt.hour > 12 ? dt.hour - 12 : dt.hour;
    const meridiem = dt.hour >= 12 ? 'PM' : 'AM';
    return {
      month: dt.month.toString(),
      day: dt.day.toString(),
      year: dt.year.toString(),
      hour: hour12.toString(),
      minute: dt.minute.toString().padStart(2, '0'),
      meridiem,
    };
  } catch {
    return {
      month: '',
      day: '',
      year: '',
      hour: '',
      minute: '',
      meridiem: 'AM',
    };
  }
}
function convertToISO(values: DateTimeValues): string | null {
  const { month, day, year, hour, minute, meridiem } = values;
  if (!month || !day || !year || !hour || !minute) {
    return null;
  }
  try {
    let hour24 = Number.parseInt(hour, 10);
    if (meridiem === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (meridiem === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    const dt = DateTime.fromObject({
      day: Number.parseInt(day, 10),
      month: Number.parseInt(month, 10),
      year: Number.parseInt(year, 10),
      hour: hour24,
      minute: Number.parseInt(minute, 10),
    });
    return dt.isValid ? dt.toISO() : null;
  } catch {
    return null;
  }
}
function areDateTimeValuesEmpty(values: DateTimeValues) {
  return (
    !values.month &&
    !values.day &&
    !values.year &&
    !values.hour &&
    !values.minute
  );
}
export type DateTimePickerProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  onBlur?: (event: BlurEvent) => void;
  minuteInterval?: number;
  testID?: string;
  error?: string;
  style?: StyleProp<ViewStyle>;
};
function DateTimePickerDivider({ value }: { value: string }) {
  return (
    <Box justifyContent="center" style={styles.divider}>
      <Text variant="typographySecondary" size="lg">
        {value}
      </Text>
    </Box>
  );
}
function DateTimePickerInput({
  maxLength,
  onBlur,
  onChangeText,
  placeholder,
  testID,
  value,
}: {
  maxLength: number;
  onBlur?: (event: BlurEvent) => void;
  onChangeText: (text: string) => void;
  placeholder: string;
  testID: string;
  value: string;
}) {
  return (
    <TextInput
      variant="plain"
      size="xl"
      inputMode="numeric"
      enterKeyHint="done"
      placeholder={placeholder}
      maxLength={maxLength}
      textAlign="center"
      onChangeText={onChangeText}
      onBlur={onBlur}
      value={value}
      selectTextOnFocus
      testID={testID}
      containerStyle={styles.dateInputCell}
      style={styles.dateInput}
    />
  );
}
function DateTimePickerYearSelect({
  onValueChange,
  testID,
  value,
}: {
  onValueChange: (value: string) => void;
  testID: string;
  value: string;
}) {
  const fallbackYear = DateTime.local().year.toString();
  const fallbackYearIndex = displayedYears.indexOf(fallbackYear);
  const fallbackIndex = fallbackYearIndex < 0 ? 0 : fallbackYearIndex;
  const selectedIndex = displayedYears.includes(value)
    ? displayedYears.indexOf(value)
    : fallbackIndex;
  const displayYear = displayedYears.includes(value) ? value : 'YYYY';
  const canDecrease = selectedIndex < displayedYears.length - 1;
  const canIncrease = selectedIndex > 0;
  const onPressDecrease = () => {
    const nextYear = displayedYears[selectedIndex + 1];

    if (canDecrease && nextYear) {
      onValueChange(nextYear);
    }
  };
  const onPressIncrease = () => {
    const nextYear = displayedYears[selectedIndex - 1];

    if (canIncrease && nextYear) {
      onValueChange(nextYear);
    }
  };

  return (
    <Box direction="horizontal" align="center" style={styles.yearStepper}>
      <Pressable
        disabled={!canDecrease}
        onPress={onPressDecrease}
        style={({ pressed }) =>
          styles.yearStepButton({ disabled: !canDecrease, pressed })
        }
        testID={`${testID}Previous`}
      >
        <Icon name="chevron-back-outline" size="lg" variant="typography" />
      </Pressable>
      <Box center style={styles.yearValue} testID={testID}>
        <Text bold size="lg" selectable={false}>
          {displayYear}
        </Text>
      </Box>
      <Pressable
        disabled={!canIncrease}
        onPress={onPressIncrease}
        style={({ pressed }) =>
          styles.yearStepButton({ disabled: !canIncrease, pressed })
        }
        testID={`${testID}Next`}
      >
        <Icon name="chevron-forward-outline" size="lg" variant="typography" />
      </Pressable>
    </Box>
  );
}

export default function DateTimePicker({
  value,
  onChange,
  onBlur,
  minuteInterval: _minuteInterval = 5,
  testID,
  style,
}: DateTimePickerProps) {
  const [datetimeValues, setDatetimeValues] = useState<DateTimeValues>(() =>
    parseISOToComponents(value || null),
  );
  const [isValid, setIsValid] = useState(false);
  const pendingValuesRef = useRef<DateTimeValues | null>(null);
  useEffect(() => {
    const nextValues = parseISOToComponents(value || null);
    const pendingValues = pendingValuesRef.current;
    const pendingIsoString = pendingValues ? convertToISO(pendingValues) : null;
    const hasPendingDateTimeEcho =
      !!value &&
      !!pendingIsoString &&
      DateTime.fromISO(value).toMillis() ===
        DateTime.fromISO(pendingIsoString).toMillis();
    const hasPendingEmptyEcho =
      !!pendingValues && !value && areDateTimeValuesEmpty(pendingValues);

    if (hasPendingDateTimeEcho || hasPendingEmptyEcho) {
      pendingValuesRef.current = null;
      return;
    }

    setDatetimeValues(nextValues);
  }, [value]);
  const handleDateTimeChange = (newValues: DateTimeValues) => {
    pendingValuesRef.current = newValues;
    setDatetimeValues(newValues);
    const isoString = convertToISO(newValues);
    if (isoString !== null) {
      onChange?.(isoString);
    } else {
      const { month, day, year, hour, minute } = newValues;
      const allEmpty = !month && !day && !year && !hour && !minute;
      if (allEmpty) {
        onChange?.(null);
      }
    }
  };
  useEffect(() => {
    const isoString = convertToISO(datetimeValues);
    setIsValid(isoString !== null);
  }, [datetimeValues]);
  const { day, month, year, hour, minute, meridiem } = datetimeValues;
  const hourAsInt = Number.parseInt(hour, 10);
  const formattedHour = hourAsInt % 12 || hourAsInt;
  const daysInMonth = getDaysInMonth({
    month,
    year,
    fallbackYear: 2024,
  });
  const onChangeMonth = (text: string) => {
    if (text.length === 1 && text === '0') {
      return;
    }
    if (!text.length) {
      handleDateTimeChange({
        ...datetimeValues,
        month: '',
      });
      return;
    }
    if (Number.isInteger(Number.parseInt(text, 10))) {
      let num = Number.parseInt(text, 10);
      if (num < 1 || !num) {
        num = 1;
      } else if (num > 12) {
        num = 12;
      }
      const monthDays = DateTime.fromObject({
        month: num,
        year: Number.parseInt(year, 10) || DateTime.local().year,
      }).daysInMonth;
      const value = String(num).replace(/^0+/, '');
      const next = {
        ...datetimeValues,
        month: value,
      };
      if (monthDays && Number.parseInt(day, 10) > monthDays) {
        next.day = monthDays.toString();
      }
      handleDateTimeChange(next);
    }
  };
  const onChangeDay = (text: string) => {
    if (text.length === 1 && text === '0') {
      return;
    }
    if (!text.length) {
      handleDateTimeChange({
        ...datetimeValues,
        day: '',
      });
      return;
    }
    if (Number.isInteger(Number.parseInt(text, 10))) {
      let num = Number.parseInt(text, 10);
      if (num < 1 || !num) {
        num = 1;
      } else if (num > daysInMonth) {
        num = daysInMonth;
      }
      const value = String(num).replace(/^0+/, '');
      handleDateTimeChange({
        ...datetimeValues,
        day: value,
      });
    }
  };
  const onChangeYear = (text: string) => {
    if (Number.isInteger(Number.parseInt(text, 10))) {
      const num = Number.parseInt(text, 10);
      const monthDays = DateTime.fromObject({
        month: Number.parseInt(month, 10) || DateTime.local().month,
        year: num,
      }).daysInMonth;
      const next = {
        ...datetimeValues,
        year: text,
      };
      if (monthDays && Number.parseInt(day, 10) > monthDays) {
        next.day = monthDays.toString();
      }
      handleDateTimeChange(next);
    }
  };
  const onChangeHour = (text: string) => {
    if (text.length === 1 && text === '0') {
      return;
    }
    if (!text.length) {
      handleDateTimeChange({
        ...datetimeValues,
        hour: '',
      });
      return;
    }
    const intValue = Number.parseInt(text, 10);
    if (Number.isInteger(intValue)) {
      let num = intValue;
      if (num < 1 || !num) {
        num = 1;
      } else if (num > 12) {
        num = 12;
      }
      handleDateTimeChange({
        ...datetimeValues,
        hour: num.toString(),
      });
    }
  };
  const onChangeMinute = (text: string) => {
    if (!text.length) {
      handleDateTimeChange({
        ...datetimeValues,
        minute: '',
      });
      return;
    }
    const num = Number.parseInt(text, 10);
    if (Number.isInteger(num)) {
      let formattedText = num.toString();
      if (text.length >= 2 && num === 0) {
        formattedText = '00';
      }
      if (num === 0) {
        formattedText = '0';
      }
      if (num < 10) {
        formattedText = `0${num}`;
      }
      if (num > 59) {
        formattedText = '59';
      }
      const next = {
        ...datetimeValues,
        minute: formattedText,
      };
      handleDateTimeChange(next);
    }
  };
  const onPressMeridiem = () => {
    const next = {
      ...datetimeValues,
      meridiem: meridiem === 'AM' ? 'PM' : 'AM',
    };
    handleDateTimeChange(next);
  };
  return (
    <Box direction="vertical" overflow="hidden" style={[styles.root, style]}>
      <Box direction="horizontal" align="center" wrap space="xs">
        <DateTimePickerInput
          maxLength={2}
          onBlur={onBlur}
          onChangeText={onChangeMonth}
          placeholder="MM"
          testID={testID ? `${testID}Month` : 'DateTimePickerMonth'}
          value={month}
        />
        <DateTimePickerDivider value="/" />
        <DateTimePickerInput
          maxLength={2}
          onBlur={onBlur}
          onChangeText={onChangeDay}
          placeholder="DD"
          testID={testID ? `${testID}Day` : 'DateTimePickerDay'}
          value={day}
        />
        <DateTimePickerDivider value="@" />
        <DateTimePickerInput
          maxLength={2}
          onBlur={onBlur}
          onChangeText={onChangeHour}
          placeholder="12"
          testID={testID ? `${testID}Hour` : 'DateTimePickerHour'}
          value={formattedHour ? formattedHour.toString() : ''}
        />
        <DateTimePickerDivider value=":" />
        <DateTimePickerInput
          maxLength={3}
          onBlur={onBlur}
          onChangeText={onChangeMinute}
          placeholder="00"
          testID={testID ? `${testID}Minute` : 'DateTimePickerMinute'}
          value={minute}
        />
        <Pressable
          disabled={!isValid}
          onPress={onPressMeridiem}
          testID={testID ? `${testID}Meridiem` : 'DateTimePickerMeridiem'}
          style={({ pressed }) =>
            styles.meridiemButton({
              disabled: !isValid,
              pressed,
            })
          }
        >
          <Text
            bold
            variant={meridiem === 'AM' ? 'warning' : 'primary'}
            size="lg"
          >
            {meridiem}
          </Text>
        </Pressable>
        <DateTimePickerYearSelect
          onValueChange={onChangeYear}
          value={year}
          testID={testID ? `${testID}Year` : 'DateTimePickerYear'}
        />
      </Box>
    </Box>
  );
}
const styles = StyleSheet.create((theme) => ({
  root: {
    alignSelf: 'center',
    width: '100%',
  },
  dateInput: {
    borderWidth: 0,
    fontWeight: theme.fontWeight.bold,
    minHeight: theme.size(10),
    paddingLeft: 0,
    paddingRight: 0,
    paddingVertical: 0,
    textAlign: 'center',
  },
  dateInputCell: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    gap: 0,
    height: theme.size(10),
    justifyContent: 'center',
    minHeight: theme.size(10),
    paddingHorizontal: 0,
    width: theme.size(12),
    _web: {
      _focusWithin: {
        borderColor: theme.colors.primary,
      },
    },
  },
  divider: {
    alignItems: 'center',
    height: theme.size(10),
    minWidth: theme.size(3),
  },
  meridiemButton: ({
    disabled,
    pressed,
  }: {
    disabled: boolean;
    pressed: boolean;
  }) => ({
    alignItems: 'center',
    backgroundColor: pressed
      ? theme.colors.surfaceSecondary
      : theme.colors.surface,
    borderColor: pressed
      ? theme.colors.borderSecondary
      : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    height: theme.size(10),
    justifyContent: 'center',
    minHeight: theme.size(10),
    opacity: disabled ? 0.4 : 1,
    overflow: 'hidden',
    width: theme.size(14),
    _web: {
      cursor: disabled ? 'not-allowed' : 'pointer',
      _hover: disabled
        ? {}
        : {
            backgroundColor: theme.colors.surfaceSecondary,
            borderColor: theme.colors.borderSecondary,
          },
    },
  }),
  yearStepButton: ({
    disabled,
    pressed,
  }: {
    disabled: boolean;
    pressed: boolean;
  }) => ({
    alignItems: 'center',
    backgroundColor: pressed ? theme.colors.surfaceSecondary : 'transparent',
    height: theme.size(10),
    justifyContent: 'center',
    opacity: disabled ? 0.4 : 1,
    width: theme.size(8),
    _web: {
      cursor: disabled ? 'not-allowed' : 'pointer',
      _hover: disabled
        ? {}
        : {
            backgroundColor: theme.colors.surfaceSecondary,
          },
    },
  }),
  yearStepper: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    height: theme.size(10),
    overflow: 'hidden',
  },
  yearValue: {
    height: theme.size(10),
    minWidth: theme.size(12),
  },
}));
